import type { DataGridFilterState, DataGridSortingState } from '@ibsys/kit';
import { useCallback, useMemo, useState } from 'react';
import type { PhraseListParams } from '@/entities/phrases';
import { DEFAULT_PHRASE_SORTING, PHRASES_PAGE_SIZE } from './constants';
import { toPhraseListParams } from './toPhraseListParams';

/** Окно, отбор и порядок перечня реплик */
type AudioAuditState = {
  /** Текущая страница, 0-based */
  page: number;
  pageSize: number;
  sorting: DataGridSortingState;
  filters: DataGridFilterState;
};

/** Пустой отбор: стабильная ссылка на первый рендер */
const EMPTY_FILTERS: DataGridFilterState = {};

const INITIAL_STATE: AudioAuditState = {
  page: 0,
  pageSize: PHRASES_PAGE_SIZE,
  sorting: DEFAULT_PHRASE_SORTING,
  filters: EMPTY_FILTERS,
};

type Result = AudioAuditState & {
  /** Условия запроса очередной страницы перечня */
  params: PhraseListParams;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSortingChange: (sorting: DataGridSortingState) => void;
  onFiltersChange: (filters: DataGridFilterState) => void;
};

/**
 * Состояние перечня реплик: окно, отбор и порядок вместе с условиями запроса.
 * Смена отбора, порядка или размера страницы возвращает перечень к первой странице —
 * прежнее смещение к новому составу отношения не имеет.
 */
export const useAudioAuditState = (): Result => {
  const [state, setState] = useState<AudioAuditState>(INITIAL_STATE);

  const params = useMemo(
    () => toPhraseListParams(state.page, state.pageSize, state.sorting, state.filters),
    [state.page, state.pageSize, state.sorting, state.filters],
  );

  const onPageChange = useCallback((page: number) => setState((prev) => ({ ...prev, page })), []);

  const onPageSizeChange = useCallback((pageSize: number) => setState((prev) => ({ ...prev, pageSize, page: 0 })), []);

  const onSortingChange = useCallback(
    (sorting: DataGridSortingState) => setState((prev) => ({ ...prev, sorting, page: 0 })),
    [],
  );

  const onFiltersChange = useCallback(
    (filters: DataGridFilterState) => setState((prev) => ({ ...prev, filters, page: 0 })),
    [],
  );

  return { ...state, params, onPageChange, onPageSizeChange, onSortingChange, onFiltersChange };
};
